import {
    JsonArticleType,
    JsonEventDayInfo,
    JsonEventPartyTravelVehicleEnum,
    JsonFile,
    JsonInviteInfoInviteTypeEnum,
    JsonInviteInfoSexEnum,
    JsonInviteReply,
    JsonInviteReplyReplyTypeEnum,
    JsonRsvp
} from "../../generated-api";
import {FormikProps} from "formik";
import {TAppFunction, useAppTranslation} from "../../services/i18n";
import {useAppDispatch} from "../../store";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {getApiResult} from "../../helpers/api";
import {saveRsvp, saveRsvpFile, saveRsvpFileTravel} from "../../store/rsvp";
import {Alert, AlertTitle, Box, Button, Card, CardActions, CardContent, FormHelperText, Grid, Table, TableBody, TableCell, TableHead, TableRow, Tooltip} from "@mui/material";
import {RadioGroupField} from "../form/RadioGroupField";
import {TextFormField} from "../form/TextFormField";
import {PhotoFormField} from "../form/PhotoFormField";
import {createOption, isAnyOtherSexSelected, OptionValue} from "../../model/form";
import {articleSize, parseParkPlates} from "../../helpers/format";
import {SimpleSelectFormField} from "../form/SimpleSelectFormField";
import {CheckboxField, CheckboxPlain} from "../form/CheckboxField";
import {CloseRounded, DoneRounded, HelpRounded, PlaylistAddCheck} from "@mui/icons-material";
import ScrollErrorsIntoView from "../form/ScrollErrorsIntoView";
import {dateToGui} from "../../helpers/date";
import RsvpInviteEventDay from "./RsvpInviteEventDay";
import RsvpInviteSupplierParties from "./RsvpInviteSupplierParties";
import {RsvpInviteTravels} from "./RsvpInviteTravels";
import {RsvpInviteComp} from "./RsvpInviteComp";
import {replyIsAcm, replyIsPark} from "../../model/invite";
import {articleSexOptions, articleTypeTitle, levenshtein, normalizeName, offeredArticleTypes} from "../../model/party";
import {renderFile} from "../../helpers/files";

const replyTypeOptions = [
    createOption(JsonInviteReplyReplyTypeEnum.Accept, 'Ano, mám zájem se události zúčastnit', undefined, undefined, 'success'),
    createOption(JsonInviteReplyReplyTypeEnum.Reject, 'Ne, o účast nemám zájem', undefined, undefined, 'error'),
];

const infoIsValidOptions = [
    createOption(true, 'Jméno a příjmení jsou v pořádku'),
    createOption(false, 'Údaje chci upravit'),
];

const eventDates = (rsvp: JsonRsvp, t: TAppFunction): string => {
    const {event, eventDays, invite} = rsvp;
    if (!event || !eventDays || !invite) {
        return '';
    }

    if (eventDays.length === 1) {
        return t('dne') + ' ' + dateToGui(eventDays[0].dayDate) || '';
    }

    return t('ve dnech') + ' ' + dateToGui(eventDays[0].dayDate) + ' - ' + dateToGui(eventDays[eventDays.length - 1].dayDate);
}

const eventYear = (rsvp: JsonRsvp): string => {
    return rsvp.event?.eventFrom?.substring(0, 4) || '';
}

const SafetyDefault = <Grid container columnSpacing={1} sx={{
    'ol > li + li': {marginTop: '10px'},
    'ul': {margin: 0, 'li': {marginTop: '5px'}}
}}>
    <Grid item md={6} xs={12}>
        <ol>
            <li><strong>Práce s elektrotechnickými zařízeními</strong>
                <ul>
                    <li>osoby proškolené, seznámené, popř. s elektrotechnickou kvalifikací
                        (odborná
                        způsobilost)
                    </li>
                    <li>kontrola stavu zařízení, krytů a přívodů</li>
                    <li>bezpečnostní kryty, organizace přívodů el. energie</li>
                    <li>hlavní vypínač vždy přístupný a viditelný</li>
                    <li>dorozumívání komunikačními prostředky – předcházení omylům</li>
                    <li>nepoužívat porušené a poškozené součásti, nářadí</li>
                    <li>seznámení s návodem k používání elektrospotřebičů</li>
                </ul>
            </li>
            <li><strong>Ruční manipulace s materiálem</strong>
                <ul>
                    <li>používání osobních ochranných pracovních prostředků (jejich
                        bezpečnost a
                        stav)
                    </li>
                    <li>přenášet pouze břemena, která jsme schopni unést (těžká břemena ve
                        dvou)
                    </li>
                    <li>věnovat pozornost terénu, ve kterém břemena přenáším</li>
                    <li>dodržovat zásady bezpečné manipulace (hygiena práce a ergonomie)
                    </li>
                </ul>
            </li>
            <li><strong>Používání ručního nářadí (vč. elektrického)</strong>
                <ul>
                    <li>požívat pouze nářadí v dobrém technickém stavu</li>
                    <li>nářadím neházet, zajistit proti pádu</li>
                    <li>používat nářadí k účelu, ke kterému je určeno</li>
                    <li>neodstraňovat bezpečnostní kryty</li>
                    <li>seznámení s návodem k používání elektrospotřebičů</li>
                </ul>
            </li>
        </ol>
    </Grid>
    <Grid item md={6} xs={12}>
        <ol start={4}>
            <li><strong>Práce s hořlavinami</strong>
                <ul>
                    <li>kontrola stavu nádob, krytů, držadel</li>
                    <li>používat osobní ochranné pracovní prostředky, na stanovišti mít k
                        dispozici
                        havarijní soupravu
                    </li>
                    <li>neskladovat v nevhodných obalech (láhve od nápojů)</li>
                    <li>při rozlití ihned odstranit (vysušit hadrem) a použít sorbent
                        (zabránit
                        vniknutí do půdy nebo vody – vodní tok)
                    </li>
                    <li>při manipulaci zákaz kouření a používání otevřeného ohně</li>
                    <li>zabránit přístupu nepovolaných osob</li>
                </ul>
            </li>
            <li><strong>Pořadatelská služba, organizátoři</strong>
                <ul>
                    <li>kvalitní obuv a vybavení (klimatické podmínky)</li>
                    <li>pohybovat se pouze po vyznačených komunikacích</li>
                    <li>dbát zvýšené opatrnosti při pohybu v těžkém terénu a věnovat
                        pozornost
                        klimatickým podmínkám (led, námraza, prohlubně a nerovný terén)
                    </li>
                    <li>při komunikaci s návštěvníky si počínat klidně a s rozvahou, zdržet
                        se
                        trestního jednání, obranné chvaty a použití ochranných prostředků
                        pouze
                        v
                        krajní nouzi – spolupráce s PČR
                    </li>
                </ul>
            </li>
        </ol>
    </Grid>
</Grid>;

type RsvpInviteFormProps = {
    rsvp: JsonRsvp,
    token: string,
    formikProps: FormikProps<JsonInviteReply>,
    isDaysLocked: boolean
}

const RsvpInviteForm = (props: RsvpInviteFormProps) => {

    const {formikProps, rsvp, token, isDaysLocked} = props;
    const {values, initialValues} = formikProps;
    const {event, invite, eventDays} = rsvp;
    const {setFieldValue} = formikProps;

    const inviteData = invite?.inviteData;
    const inviteSex = invite?.sex;

    const t = useAppTranslation();
    const dispatch = useAppDispatch();
    const [isArticleOtherSex, setIsArticleOtherSex] = useState(isAnyOtherSexSelected(inviteSex, initialValues.replyData?.articles));
    const [showArticleAllSex, setShowArticleAllSex] = useState(isArticleOtherSex);
    const [parsedParkPlates, setParsedParkPlates] = useState<string[]>([]);

    const onReplySave = useCallback(async (reply: JsonInviteReply) => {
        return getApiResult(await dispatch(saveRsvp({token, reply})));

    }, [token, dispatch]);

    const onFileUpload = useCallback(async (file: File, replyValues: JsonInviteReply, isTravel?: boolean) => {
        if (isTravel) {
            return getApiResult<JsonFile>(await dispatch(saveRsvpFileTravel({token, file})));
        }
        if (await onReplySave(replyValues)) {
            const fileRes = await dispatch(saveRsvpFile({token, file}));
            return getApiResult<JsonFile>(fileRes);
        }

    }, [token, onReplySave, dispatch]);

    const articleTypeOptions = useMemo(() => {
        const articleTypeOptions: { [key in number]: OptionValue[] } = {};
        inviteData?.articleTypes?.forEach((at) => {
            if (!at.articleTypeId || !at.variants?.sizes) {
                return;
            }
            const options: OptionValue[] = [];
            if (at?.variants?.sizes) {
                Object.keys(at.variants.sizes).forEach((sex) => {
                    if (!showArticleAllSex && sex !== 'U' && !!inviteSex && inviteSex !== sex as JsonInviteInfoSexEnum) {
                        return; // do not offer opposite sex articles
                    }
                    if (sex === 'U' && at.variants?.sizes?.['M'] && at.variants.sizes['F']) {
                        return; // ignore U if M/F are present
                    }
                    const sexTitle = showArticleAllSex && articleSexOptions.find((o) => o.value === sex)?.tooltip;
                    const sizes = at.variants?.sizes ? at.variants.sizes[sex] : undefined;
                    sizes?.forEach((size) => {
                        const article = inviteData?.articles?.find((a) => a.articleTypeId === at.articleTypeId && a.size === size && a.sex === sex);
                        if (article?.articleId) {
                            options.push(createOption(article.articleId, articleSize(sex, size) + (sexTitle ? ' ' + sexTitle : '')));
                        }
                    })
                });
            }
            if (at.articleTypeId) {
                articleTypeOptions[at.articleTypeId] = options;
            }
        })
        return articleTypeOptions;
    }, [inviteData, inviteSex, showArticleAllSex]);

    const gdpr = useMemo(() => {
        if (invite?.inviteType === JsonInviteInfoInviteTypeEnum.CompOrg || invite?.inviteType === JsonInviteInfoInviteTypeEnum.SafetyOrg) {
            return null;
        }
        const q = invite?.inviteType === JsonInviteInfoInviteTypeEnum.Organizer || invite?.inviteType === JsonInviteInfoInviteTypeEnum.UpdateOrg
            ? t('Potvrzuji, že mnou uvedené informace odpovídají skutečnosti, a souhlasím se zpracováním osobních údajů pro účely účasti na sportovní události.')
            : (invite?.inviteType === JsonInviteInfoInviteTypeEnum.TravelOrg
                ? !!values.replyData?.travels?.find((t) => t.vehicle === JsonEventPartyTravelVehicleEnum.Car)
                    ? t('Prohlašuji, že výše uvedené údaje jsou správné a souhlasím s proplacením cestovného podle směrnice ČSB o výši náhrad jízdného a stravného pro pořadatele soutěží ve výši 5,20 Kč za 1 km (automobil vlastní).')
                    : t('Prohlašuji, že výše uvedené údaje jsou správné a souhlasím s proplacením cestovného podle směrnice ČSB o výši náhrad jízdného a stravného pro pořadatele soutěží.')
                : t('Potvrzuji, že mnou uvedené informace odpovídají skutečnosti, a souhlasím se zpracováním osobních údajů pro účely umožnění přístupu na sportovní událost.'));
        return <Grid container>
            <Grid item xs={11}>
                <CheckboxField name={'replyData.isGdpr'} color={'default'} label={q}/>
            </Grid>
            <Grid item xs={1}>
                <Tooltip title={<div>
                    <p>{t('gdpr1')}</p>
                    <p>{t('gdpr2')}</p>
                </div>} placement={'top-end'} enterDelay={1000} arrow
                    PopperProps={{sx: {'& .MuiTooltip-tooltip': {maxWidth: 'clamp(200px, 600px, 80vw)'}}}}>
                    <HelpRounded sx={{fontSize: '150%', margin: '0 0 -5px 5px', color: 'var(--dark-gray)'}}/>
                </Tooltip>
            </Grid>
        </Grid>
    }, [invite?.inviteType, values.replyData?.travels, t]);

    const articleSelectCallback = useCallback((at: JsonArticleType, i: number, v: number) => {
        const a = inviteData?.articles?.find((a) => a.articleId === v);
        const article = {
            articleTypeId: at.articleTypeId,
            size: a?.size,
            sex: a?.sex
        };
        setFieldValue('replyData.articles.' + i + '.articleTypeId', article.articleTypeId, false);
        setFieldValue('replyData.articles.' + i + '.size', article.size, false);
        setFieldValue('replyData.articles.' + i + '.sex', article.sex, false);
        const newValues = values?.replyData?.articles || [];
        newValues[i] = article;
        setIsArticleOtherSex(isAnyOtherSexSelected(inviteSex, newValues));
    }, [setFieldValue, inviteData?.articles, inviteSex, values?.replyData?.articles]);

    const articleFormTables = useMemo(() => {
        const visibleTypes = offeredArticleTypes(inviteData?.articleTypes);
        if (!visibleTypes.length) {
            return null;
        }
        const tables = [];
        const ROW_SIZE = 4;
        const rowCount = Math.ceil(visibleTypes.length / ROW_SIZE);

        const padding: number[] = [];
        for (let i = visibleTypes.length % ROW_SIZE; i > 0 && i < ROW_SIZE; i++) {
            padding.push(i);
        }

        for (let p = 0; p < rowCount; p++) {
            const types = visibleTypes.slice(p * ROW_SIZE, (p + 1) * ROW_SIZE);
            tables.push(<Table key={p} className={'data-grid'}>
                <TableHead>
                    <TableRow>
                        {types.map(({at}, i) => {
                            return <TableCell key={i}>
                                <div style={{padding: '10px'}}>{articleTypeTitle(at.title)}</div>
                            </TableCell>
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow sx={{
                        '&:hover td': {
                            background: 'inherit !important',
                            borderBottom: '1px solid var(--gray-background) !important'
                        }
                    }}>
                        {types.map(({at, index}, i) => {
                            if (!at.articleTypeId || !at.variants?.sizes) {
                                return <TableCell key={i}>-</TableCell>;
                            }
                            // replyData.articles mirrors the full articleTypes list, not the offered subset
                            const articleIndex = index;
                            return <TableCell key={i}>
                                <div style={{padding: '10px'}}><SimpleSelectFormField
                                    placeholder='Velikost'
                                    name={'replyData.articles.' + articleIndex + '.articleId'}
                                    onChange={(v) => {
                                        articleSelectCallback(at, articleIndex, v);
                                    }}
                                    options={articleTypeOptions[at.articleTypeId]}/></div>
                            </TableCell>;
                        })}
                        {p === rowCount - 1 && padding.map((i) => {
                            return <TableCell key={i}></TableCell>
                        })}
                    </TableRow>
                </TableBody>
            </Table>);
        }
        return tables;

    }, [inviteData?.articleTypes, articleTypeOptions, articleSelectCallback]);

    const showIdentityWarning = useMemo(() => {
        if (values.infoIsValid) {
            return false;
        }
        const oldName = normalizeName(invite?.firstName?.toLowerCase());
        const newName = normalizeName(values.newFirstName?.toLowerCase());

        return (values.newPhotoGuid && values.newPhotoGuid !== invite?.photoGuid)
            && (oldName !== newName && levenshtein(oldName, newName) > 3);

    }, [values.infoIsValid, values.newPhotoGuid, values.newFirstName, invite?.firstName, invite?.photoGuid]);

    if (!rsvp || !event || !invite || !eventDays) {
        return null;
    }

    const safetyAttachments = <>
        <h2>{t('Přílohy školení Bezpečnosti a ochrany zdraví při práci a Požární ochrany')}</h2>
        <Box sx={{'div': {padding: '5px 0'}}}>
            {rsvp.attachments?.map(a => <div key={a.fileId}>{renderFile(a)}</div>)}
        </Box>
    </>;
    const safetyConfirmText = rsvp.template?.templateData?.confirmText
        || (rsvp.attachments?.length
            ? t('Potvrzuji, že jsem byl proškolen a seznámen s předpisy BOZP a PO vztahujícími se ke Světovému poháru v biatlonu {{year}}.', {year: eventYear(rsvp)})
            : t('Potvrzuji, že jsem byl proškolen a seznámen s předpisy BOZP a PO vztahujícími se ke Světovému poháru v biatlonu {{year}}, podle uvedené osnovy.', {year: eventYear(rsvp)}));
    const isOrgSafety = invite.inviteType === JsonInviteInfoInviteTypeEnum.Organizer
        && values.replyType === JsonInviteReplyReplyTypeEnum.Accept
        && !!rsvp.attachments?.length;

    let title;
    let header;
    let isPersonal;
    let isArticles: boolean;
    if (invite.inviteType === JsonInviteInfoInviteTypeEnum.UpdateOrg) {
        title = (event.title?.toLowerCase && event.title?.toLowerCase().indexOf('výzva') >= 0)
            ? event.title
            : <span>{t('Výzva k doplnění údajů')} {event.title} </span>;
        header = <Card>
            <CardContent className={'rsvp-header'}>
                <p dangerouslySetInnerHTML={{__html: t('Děkujeme za váš zájem účastnit se a pomáhat při organizaci události v biatlonu v Novém Městě na Moravě {{dates}}.', {dates: eventDates(rsvp, t)})}}/>
                <p>{t('Zkontrolujte prosím všechny údaje uvedené v tomto formuláři a v případě potřeby je doplňte či aktualizujte, děkujeme.')}</p>
                {!!invite.processNote &&
                    <Alert severity={'warning'}>
                        <AlertTitle>{t('Výzva vám byla vrácena k doplnění')}</AlertTitle>
                        <blockquote>{invite.processNote}</blockquote>
                    </Alert>
                }
            </CardContent>
        </Card>;
        isPersonal = true;
        isArticles = true;

    } else if (invite.inviteType === JsonInviteInfoInviteTypeEnum.SafetyOrg) {
        title = (event.title?.toLowerCase && event.title?.toLowerCase().indexOf('bozp') >= 0)
            ? event.title
            : <span>{t('Výzva k potvrzení o proškolení BOZP')} {event.title} </span>;
        header = <Card>
            <CardContent className={'rsvp-header'}>
                <p dangerouslySetInnerHTML={{__html: t('Prosíme Vás o potvrzení proškolení s předpisy BOZP a PO vztahujícími se k události v biatlonu v Novém Městě na Moravě {{dates}}.', {dates: eventDates(rsvp, t)})}}/>
                {rsvp.attachments?.length
                    ? null
                    : <p>{t('Proškolení provedl vedoucí skupiny na úvodním setkání organizátorů.')}</p>}
            </CardContent>
        </Card>;
        isPersonal = false;
        isArticles = false;

    } else if (invite.inviteType === JsonInviteInfoInviteTypeEnum.TravelOrg) {
        title = (event.title?.toLowerCase && event.title?.toLowerCase().indexOf('výzva') >= 0)
            ? event.title
            : <span>{t('Výzva k zadání cestovních náhrad')} {event.title} </span>;
        header = <Card>
            <CardContent className={'rsvp-header'}>
                <p dangerouslySetInnerHTML={{__html: t('Děkujeme za vaši pomoc při organizaci události v biatlonu v Novém Městě na Moravě {{dates}}.', {dates: eventDates(rsvp, t)})}}/>
                <p>{t('Pokud si přejete uplatnit nárok na vyplacení cestovních náhrad, zadejte je prosím níže do formuláře.')}</p>
                {!!invite.processNote &&
                    <Alert severity={'warning'}>
                        <AlertTitle>{t('Výzva vám byla vrácena k doplnění')}</AlertTitle>
                        <blockquote>{invite.processNote}</blockquote>
                    </Alert>
                }
            </CardContent>
        </Card>;
        isPersonal = false;
        isArticles = false;

    } else if (invite.inviteType === JsonInviteInfoInviteTypeEnum.CompOrg) {
        title = (event.title?.toLowerCase && event.title?.toLowerCase().indexOf('výzva') >= 0)
            ? event.title
            : <span>{t('Výzva k potvrzení výplaty')} {event.title} </span>;
        header = <Card>
            <CardContent className={'rsvp-header'}>
                {rsvp.template?.bodyTop
                    ? <p dangerouslySetInnerHTML={{__html: rsvp.template.bodyTop}}/>
                    : <><p dangerouslySetInnerHTML={{
                        __html:
                            t('Děkujeme za vaši pomoc při organizaci události v biatlonu v Novém Městě na Moravě {{dates}}.', {dates: eventDates(rsvp, t)})
                    }}/>
                        <p>{t('Potvrďte prosím, zda souhlasíte s níže navrženou výší celkové výplaty, a ověřte správnost čísla bankovního účtu, na které budou prostředky poukázány.')}</p>
                    </>}
                {!!invite.processNote &&
                    <Alert severity={'warning'}>
                        <AlertTitle>{t('Výzva byla vrácena k nové kontrole')}</AlertTitle>
                        <blockquote>{invite.processNote}</blockquote>
                    </Alert>
                }
            </CardContent>
        </Card>;
        isPersonal = false;
        isArticles = false;

    } else if (invite.inviteType === JsonInviteInfoInviteTypeEnum.Supplier) {
        title = <span>{t('Doplnění personálu pro ')} {event.title} </span>;
        header = <Card>
            <CardContent className={'rsvp-header'}>
                <p dangerouslySetInnerHTML={{__html: t('Prosíme vás o potvrzení vaší účasti a doplnění všech dalších osob personálu, pro které požadujete akreditaci na událost v biatlonu v Novém Městě na Moravě {{dates}}.', {dates: eventDates(rsvp, t)})}}/>
                {!!invite.processNote &&
                    <Alert severity={'warning'}>
                        <AlertTitle>{t('Formulář vám byl vrácen k doplnění')}</AlertTitle>
                        <blockquote>{invite.processNote}</blockquote>
                    </Alert>
                }
            </CardContent>
        </Card>;
        isPersonal = true;
        isArticles = false;

    } else {
        title = (event.title?.toLowerCase && event.title?.toLowerCase().indexOf('pozvánka') >= 0)
            ? event.title
            : <span>{t('Pozvánka na událost')} {event.title} </span>;
        header = <Card>
            <CardContent className={'rsvp-header'}>
                <p dangerouslySetInnerHTML={{__html: t('Rádi bychom vás pozvali na událost v biatlonu <strong>{{dates}}</strong>.', {dates: eventDates(rsvp, t)})}}/>
                {!!inviteData?.extraLimit && inviteData.extraLimit > 0
                    && <p dangerouslySetInnerHTML={{
                        __html: eventDays.length > 1
                            ? (inviteData.extraLimit > 1
                                ? (inviteData.extraLimit > 4
                                    ? t('Na každý den je možné s sebou vzít <strong>až {{extraLimit}} dalších osob jako doprovod</strong> (mohou se libovolně opakovat a/nebo být každý den různé).', inviteData)
                                    : t('Na každý den je možné s sebou vzít <strong>doprovod až {{extraLimit}} další osoby</strong> (mohou se libovolně opakovat a/nebo být každý den různé).', inviteData))
                                : t('Na každý den je možné s sebou vzít <strong>doprovod</strong> (může být vždy stejný a/nebo každý den jiný).', inviteData))
                            : (inviteData.extraLimit > 1
                                ? (inviteData.extraLimit > 4
                                    ? t('Je možné s sebou vzít <strong>až {{extraLimit}} dalších osob jako doprovod</strong>.', inviteData)
                                    : t('Je možné s sebou vzít <strong>doprovod až {{extraLimit}} další osoby</strong>.', inviteData))
                                : t('Je možné s sebou vzít <strong>doprovod</strong>.', inviteData))
                    }}/>}
                {invite.inviteType === JsonInviteInfoInviteTypeEnum.Organizer && invite.groupTitle && <p>
                    {t('Na událost Vás zveme do skupiny') + ' '}
                    <strong>{invite.groupTitle}</strong>
                </p>}
                {!!invite.processNote &&
                    <Alert severity={'warning'}>
                        <AlertTitle>{t('Pozvánka Vám byla vrácena k doplnění')}</AlertTitle>
                        <blockquote>{invite.processNote}</blockquote>
                    </Alert>
                }
            </CardContent>
            {!(isDaysLocked && values.replyType === JsonInviteReplyReplyTypeEnum.Accept) &&
                <CardActions className={'rsvp-reply-type'}>
                    <RadioGroupField options={replyTypeOptions} name={'replyType'}/>
                </CardActions>}
        </Card>;
        isPersonal = values.replyType === JsonInviteReplyReplyTypeEnum.Accept;
        isArticles = values.replyType === JsonInviteReplyReplyTypeEnum.Accept && invite.inviteType === JsonInviteInfoInviteTypeEnum.Organizer;
    }

    return <form onSubmit={formikProps.handleSubmit}>
        <h1>{title}</h1>
        {header}
        {isPersonal && <Card>
            <CardContent>
                <Grid container className={'rsvp-info'}>
                    <Grid item sm={8}>
                        <p>{t('Ověřte prosím správnost vašich osobních údajů, které budou společně s fotografií vytištěny na vaší přístupové kartě.')}</p>
                        <Grid container>
                            <Grid item sm={5} xs={12}>
                                <TextFormField name={'newFirstName'} label={'Jméno'} readonly={values.infoIsValid !== false}/>
                                <TextFormField name={'newLastName'} label={'Příjmení'} readonly={values.infoIsValid !== false}/>
                            </Grid>
                            <Grid item xs={12}>
                                <RadioGroupField options={infoIsValidOptions} name={'infoIsValid'} onChange={(v) => {
                                    if (v) {
                                        setTimeout(() => {
                                            formikProps.setFieldValue('newFirstName', invite?.firstName, false);
                                            formikProps.setFieldValue('newLastName', invite?.lastName, false);
                                        }, 100);
                                    }
                                }}/>
                            </Grid>
                            {showIdentityWarning && <Grid item xs={12}>
                                <Alert severity={'warning'} icon={false}>
                                    <p>{t('Upozornění')}: {t('Pozvánka je nepřenosná, není tedy povolena změna identity hosta a nahrávání fotografie jiné osoby. V takovém případě bude pozvánka vrácena k přepracování.')}</p>
                                </Alert>
                            </Grid>}
                        </Grid>
                    </Grid>
                    <Grid item sm={4}>
                        <p>{invite.photoGuid
                            ? t('Ověřte také aktuálnost níže uvedené fotografie a případně nahrajte novou.')
                            : t('Tlačítkem níže prosím nahrajte svou aktuální fotografii.')}</p>
                        <PhotoFormField name={'newPhotoGuid'}
                            title={((values.newFirstName || '') + ' ' + (values.newLastName || '')).trim()}
                            onRequestChange={async (file) => {
                                return await onFileUpload(file, values);
                            }}
                            onChange={async (file: JsonFile) => {
                                values.newPhotoGuid = file?.guid;
                                values.newPhotoCvResult = file?.cvResult;
                                await onReplySave(values);
                            }}
                            defaultValue={invite.photoGuid}/>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>}
        {values.replyType === JsonInviteReplyReplyTypeEnum.Accept && invite.inviteType === JsonInviteInfoInviteTypeEnum.Guest && <>
            <h4>
                {eventDays.length > 1
                    ? t('Potvrďte prosím účast na každém níže uvedeném dni akce')
                    : t('Potvrďte prosím účast na uvedeném dni akce')}
                {!!inviteData?.extraLimit && inviteData.extraLimit > 0 && t(' a vyberte případný doprovod')}:
            </h4>

            {eventDays.map((ed: JsonEventDayInfo) => {
                return <RsvpInviteEventDay key={ed.dayNo} inviteData={inviteData} values={values} ed={ed} prevParties={rsvp.prevParties}
                    onFileUpload={onFileUpload} onReplySave={onReplySave} isDaysLocked={isDaysLocked}/>
            })}
        </>}
        {isArticles && !!articleFormTables?.length && <>
            <Card>
                <CardContent>
                    <p>{t('Vyberte prosím vaši preferovanou velikost oblečení:')}</p>
                    <em style={{display: 'block', paddingTop: '5px'}}>{t('Zobrazené velikosti: {{sex}}', {
                        sex: showArticleAllSex ? 'dámské i pánské' : (inviteSex === JsonInviteInfoSexEnum.F ? 'dámské' : 'pánské')
                    })}</em>
                    {!isArticleOtherSex &&
                        <CheckboxPlain name={'isArticleAllSex'} currentValue={showArticleAllSex} label={t('Zobrazit dámské i pánské velikosti')} onChange={setShowArticleAllSex}/>}
                    {articleFormTables}
                </CardContent>
            </Card>
            {/*{!inviteData?.isUser && <Card className={'rsvp-info'}>*/}
            {/*	<CardContent>*/}
            {/*		<p>{t('Po potvrzení účasti Vám bude zřízen přístup do systému EMS, kde budete mít následně možnost spravovat agendu spojenou s událostí.')}</p>*/}
            {/*	</CardContent>*/}
            {/*</Card>}*/}
        </>}
        {values.replyType === JsonInviteReplyReplyTypeEnum.Accept && invite.inviteType === JsonInviteInfoInviteTypeEnum.Organizer && <>
            <Card>
                <CardContent>
                    <p>{t('Potřebujete zajistit ubytování?')}</p>
                    <Grid container columnSpacing={1}>
                        <Grid item xs={12} sx={{marginTop: '10px'}}>
                            <RadioGroupField name={'replyData.isAcm'} options={replyIsAcm}/>
                        </Grid>
                        {/*{!!values.replyData?.isAcm && <Grid item xs={12}>*/}
                        {/*	<p>*/}
                        {/*		<em>{t('Preferujeme zájemce s vlastním ubytováním v NMNM nebo okolí. Máme omezenou kapacitu ubytování jen pro výjimečné případy. Pokud budeme se zajištěním ubytování pomáhat, počítejte se sdílením nákladů.')}</em>*/}
                        {/*	</p>*/}
                        {/*</Grid>}*/}
                    </Grid>
                </CardContent>
            </Card>
            <Card>
                <CardContent>
                    <p>{t('Máte zájem o parkování?')}</p>
                    <Grid container columnSpacing={1}>
                        <Grid item xs={12} sx={{marginTop: '10px'}}>
                            <RadioGroupField name={'replyData.isPark'} options={replyIsPark}/>
                        </Grid>
                        {!!values.replyData?.isPark && <Grid item xs={12}>
                            <Grid container columnSpacing={2} rowSpacing={1}>
                                <Grid item sm={4} xs={6}>
                                    <TextFormField name={'replyData.parkPlates'} label={t('SPZ')} type={'text'} placeholder={'1A2ABCD'} maxlength={16}/>
                                </Grid>
                            </Grid>
                            <p>
                                <em>{t('Registrační značku je možné zadat či změnit i později prostřednictvím vašeho Dashboardu zde v EMS. Změnu na vyžádání schvaluje kancelář. Bez zadané RZ nebude možné vydat parkovací kartu.')}</em>
                            </p>
                        </Grid>}
                    </Grid>
                </CardContent>
            </Card>
        </>}
        {invite.inviteType === JsonInviteInfoInviteTypeEnum.Supplier && <>
            <Card>
                <CardContent>
                    <p>{t('Máte zájem o parkování?')}</p>
                    <Grid container columnSpacing={1}>
                        <Grid item xs={12} sx={{marginTop: '10px'}}>
                            <RadioGroupField name={'replyData.isPark'} options={replyIsPark}/>
                        </Grid>
                        {!!values.replyData?.isPark && <Grid item xs={12}>
                            <Grid container columnSpacing={5} rowSpacing={2} sx={{marginTop: '10px', marginBottom: '10px'}}>
                                <Grid item xs={12}>
                                    <Grid container rowSpacing={2}>
                                        <Grid item xs={12}>
                                            <p>{t('Zadejte celkový počet vozidel, které bude potřeba zaparkovat:')}</p>
                                        </Grid>
                                        <Grid item xs={2}>
                                            <TextFormField name={'replyData.parkTotalCnt'} label={t('Vozidel celkem')} type={'number'}
                                                placeholder={'3'} maxlength={3} minValue={1} maxValue={99}/>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                {!!values.replyData?.parkTotalCnt && values.replyData?.parkTotalCnt > 0 && <Grid item xs={12}>
                                    <Grid container rowSpacing={2}>
                                        <Grid item xs={12}>
                                            <p>{t('Zadejte jejich SPZ (pokud nějaké zatím nevíte, doplníte později)')}:</p>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextFormField name={'replyData.parkPlates'}
                                                label={t('Seznam SPZ (oddělte čárkou)')}
                                                type={'textarea'}
                                                placeholder={'1A2ABCD, 1A2EFGH'}
                                                maxlength={500}
                                                onChange={v => setParsedParkPlates(parseParkPlates(v))}
                                                onBlur={(v) => setFieldValue('replyData.parkPlates', parseParkPlates(v).join(', '), false)}
                                            />
                                        </Grid>
                                        {!!parsedParkPlates.length && <Grid item xs={12}>
                                            <Alert severity={parsedParkPlates.length === +values.replyData.parkTotalCnt ? 'success' : 'warning'}>
                                                <p>
                                                    {t('Zadáno SPZ')}: <strong title={parsedParkPlates.join(', ')}>{parsedParkPlates.length}</strong> z <strong>{values.replyData?.parkTotalCnt}</strong>.
                                                    <span> {t('Seznam bude možné doplnit či změnit i později při akreditaci.')}</span>
                                                </p>
                                            </Alert>
                                        </Grid>}
                                        <Grid item xs={12}>
                                            <Alert severity={'info'}>
                                                <p>{t('Bez zadané SPZ nebude možné vydat parkovací kartu.')}</p>
                                            </Alert>
                                        </Grid>
                                    </Grid>
                                </Grid>}
                            </Grid>
                        </Grid>}
                    </Grid>
                </CardContent>
            </Card>
        </>}
        {((values.replyType === JsonInviteReplyReplyTypeEnum.Accept && invite.inviteType === JsonInviteInfoInviteTypeEnum.Organizer)
            || invite.inviteType === JsonInviteInfoInviteTypeEnum.UpdateOrg) && <Card>
            <CardContent>
                <p>{t('Proveďte prosím kontrolu a doplnění níže uvedených údajů pro účely vytvoření smlouvy:')}</p>
                <Grid container columnSpacing={1}>
                    <Grid item xs={12} sx={{marginTop: '10px'}}>
                        <Grid container columnSpacing={2} rowSpacing={1}>
                            <Grid item sm={4} xs={12}>
                                <TextFormField name={'replyData.partyData.bankAccount'} label={t('Číslo bankovního účtu')} type={'text'}/>
                                <FormHelperText>{t('12345678/1234, 123-4567890/1234 atp.')}</FormHelperText>
                            </Grid>
                            <Grid item sm={3} xs={12}>
                                <TextFormField name={'replyData.partyData.birthDate'} label={t('Datum narození')} type={'date'}/>
                            </Grid>
                            <Grid item sm={3} xs={12}>
                                <TextFormField name={'replyData.partyData.phone'} label={t('Telefon')} type={'text'} maxlength={16}/>
                                <FormHelperText>{t('777123456, +421900123456 atp.')}</FormHelperText>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sx={{marginTop: '-10px'}}>
                        <h4>{t('Adresa trvalého bydliště')}</h4>
                        <Grid container columnSpacing={2} rowSpacing={1}>
                            <Grid item sm={4} xs={12}>
                                <TextFormField name={'replyData.partyData.permAddress.street'} label={t('Ulice a číslo')} type={'text'}/>
                                <FormHelperText>{t('Sportovní 12/34, Hoštice 12 atp.')}</FormHelperText>
                            </Grid>
                            <Grid item sm={2} xs={6}>
                                <TextFormField name={'replyData.partyData.permAddress.zip'} label={t('PSČ')} type={'text'} maxlength={10}/>
                                <FormHelperText>{t('132 45')}</FormHelperText>
                            </Grid>
                            <Grid item sm={6} xs={12}>
                            </Grid>
                            <Grid item sm={4} xs={12}>
                                <TextFormField name={'replyData.partyData.permAddress.city'} label={t('Obec')} type={'text'}/>
                                <FormHelperText>{t('Nové Město na Moravě, Vyškov atp.')}</FormHelperText>
                            </Grid>
                            <Grid item sm={2} xs={6}>
                                <TextFormField name={'replyData.partyData.permAddress.country'} label={t('Stát')} type={'text'}/>
                                <FormHelperText>{t('CZ, SK atp.')}</FormHelperText>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>}
        {isOrgSafety && <Card>
            <CardContent>
                <Grid container columnSpacing={1}>
                    <Grid item xs={12} sx={{h2: {margin: 0}}}>
                        {safetyAttachments}
                    </Grid>
                    <Grid item xs={12} sx={{marginTop: '10px'}}>
                        <CheckboxField name={'replyData.isSafety'} color={'default'}
                            label={safetyConfirmText}/>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>}
        {invite.inviteType === JsonInviteInfoInviteTypeEnum.SafetyOrg && !!event.eventId && <>
            <Card>
                <CardContent>
                    <Grid container columnSpacing={1}>
                        <Grid item xs={12} sx={{h2: {margin: 0}}}>
                            {rsvp.attachments?.length
                                ? safetyAttachments
                                : <>
                                    <h2>Osnova školení Bezpečnosti a ochrany zdraví při práci a Požární ochrany</h2>
                                    {SafetyDefault}
                                </>}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <Card>
                <CardContent>
                    <Grid container columnSpacing={1}>
                        <Grid item xs={12}>
                            <CheckboxField name={'replyData.isSafety'} color={'default'}
                                label={safetyConfirmText}/>
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions className={'rsvp-buttons'}>
                    <Button variant="contained" type="submit"
                        onClick={() => {
                            formikProps.handleSubmit();
                        }}
                        color={'success'}
                        disabled={formikProps.isSubmitting}><DoneRounded/>
                        <span>{t('Odeslat potvrzení')}</span></Button>
                </CardActions>
            </Card>
        </>}
        {invite.inviteType === JsonInviteInfoInviteTypeEnum.TravelOrg && !!event.eventId && <Card>
            <RsvpInviteTravels token={token} eventId={event.eventId} values={values}
                onFileUpload={onFileUpload} onReplySave={onReplySave}/>
        </Card>}
        {invite.inviteType === JsonInviteInfoInviteTypeEnum.CompOrg && !!event.eventId && !!rsvp.eventParties?.[0] &&
            <Card>
                <RsvpInviteComp values={values} eventParty={rsvp.eventParties[0]} tariffs={rsvp.tariffs}/>
            </Card>}
        {invite.inviteType === JsonInviteInfoInviteTypeEnum.Supplier && !!event.eventId && <>
            <RsvpInviteSupplierParties token={token} eventId={event.eventId} values={values}
                onFileUpload={onFileUpload} onReplySave={onReplySave}/>
        </>}
        {(invite.inviteType === JsonInviteInfoInviteTypeEnum.UpdateOrg || invite.inviteType === JsonInviteInfoInviteTypeEnum.TravelOrg) &&
            <Card>
                <CardContent>
                    {gdpr}
                </CardContent>
                <CardActions className={'rsvp-buttons'}>
                    <Button variant="contained" type="submit"
                        color={'success'}
                        disabled={formikProps.isSubmitting}><PlaylistAddCheck/>
                        <span>{t('Pokračovat na shrnutí a odeslání')}</span></Button>
                </CardActions>
            </Card>}
        {invite.inviteType === JsonInviteInfoInviteTypeEnum.CompOrg && values.replyType && <Card>
            <CardContent>
                {values.replyType === JsonInviteReplyReplyTypeEnum.Reject &&
                    <TextFormField name={'replyNote'} type={'textarea'} rows={5} maxlength={1000}
                        label={'Zdůvodnění'}/>}
            </CardContent>
            <CardActions className={'rsvp-buttons'}>
                <Button variant="contained" type="button"
                    onClick={() => {
                        formikProps.setFieldValue('replyData.eventCompDetails', rsvp.eventParties?.[0]?.eventCompDetails);
                        formikProps.setFieldValue('replyData.eventTravelDetails', rsvp.eventParties?.[0]?.eventTravelDetails);
                        formikProps.setFieldValue('replyData.partyData.bankAccount', rsvp.eventParties?.[0]?.bankAccount);
                        formikProps.handleSubmit();
                    }}
                    color={'success'}
                    disabled={formikProps.isSubmitting}><PlaylistAddCheck/>
                    <span>{t('Pokračovat na shrnutí a odeslání')}</span></Button>
            </CardActions>
        </Card>}
        {invite.inviteType !== JsonInviteInfoInviteTypeEnum.UpdateOrg
            && invite.inviteType !== JsonInviteInfoInviteTypeEnum.SafetyOrg
            && invite.inviteType !== JsonInviteInfoInviteTypeEnum.CompOrg
            && values.replyType &&
            <Card>
                <CardContent>
                    <TextFormField name={'replyNote'} type={'textarea'} rows={5} maxlength={1000}
                        label={'Pokud si přejete uvést doplňující informace, napiště je prosím zde'}/>
                    {values.replyType === JsonInviteReplyReplyTypeEnum.Accept && gdpr}
                </CardContent>
                <CardActions className={'rsvp-buttons'}>
                    <Button variant="contained" type="submit"
                        color={values.replyType === JsonInviteReplyReplyTypeEnum.Accept ? 'success' : 'error'}
                        disabled={formikProps.isSubmitting}>{values.replyType === JsonInviteReplyReplyTypeEnum.Accept
                        ? <><PlaylistAddCheck/> <span>{t('Pokračovat na shrnutí a odeslání')}</span></>
                        : <><CloseRounded/> <span>{t('Odeslat odmítnutí účasti')}</span></>}</Button>
                </CardActions>
            </Card>}
        <ScrollErrorsIntoView/>
    </form>;
}

export default RsvpInviteForm;
